import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filters.applied');

const MAX_TEXT_LENGTH = 70;
const ELLIPSIS = '...';
const MIN_SUBJECT_LENGTH = 24;

// Strip HTML tags for plain text display in tooltips
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

// Preserve only <strong> tags while stripping all other HTML tags
const preserveStrongTags = (html: string): string => {
  const withPlaceholders = html
    .replace(/<strong>/g, '___STRONG_OPEN___')
    .replace(/<\/strong>/g, '___STRONG_CLOSE___');

  const stripped = stripHtmlTags(withPlaceholders);

  return stripped
    .replace(/___STRONG_OPEN___/g, '<strong>')
    .replace(/___STRONG_CLOSE___/g, '</strong>');
};

const truncatePlainText = (text: string, maxLength: number): string => {
  if (text.length <= Math.max(maxLength, 0)) {
    return text;
  }

  const kept = text.slice(0, Math.max(maxLength - ELLIPSIS.length, 0));
  return `${kept.trimEnd()}${ELLIPSIS}`;
};

// Filter text is "<strong>subject operator</strong> selections": a raw slice can cut inside
// the tag, so the subject gives up characters first and the selections survive.
const truncatePreservingTags = (html: string, maxLength: number): string => {
  const parts = /^<strong>([^]*)<\/strong>([^]*)$/.exec(html);
  if (!parts) {
    return truncatePlainText(html, maxLength);
  }

  const [, subject, selections] = parts;
  const trimmedSubject = truncatePlainText(
    subject,
    Math.max(MIN_SUBJECT_LENGTH, maxLength - selections.length)
  );

  return `<strong>${trimmedSubject}</strong>${truncatePlainText(
    selections,
    maxLength - trimmedSubject.length
  )}`;
};

type Props = {
  text?: string | null;
  onRemove: () => void;
};

const ClearableFilter = ({ text, onRemove }: Props) => {
  if (!text) {
    return (
      <IconButton
        icon={CloseIcon}
        label={t('remove')}
        onClick={onRemove}
        size="sm"
        flat
      />
    );
  }

  const plainText = stripHtmlTags(text);
  const markup = preserveStrongTags(text);
  const isTruncated = plainText.length > MAX_TEXT_LENGTH;
  const displayText = isTruncated
    ? truncatePreservingTags(markup, MAX_TEXT_LENGTH)
    : markup;

  const tooltipText: string | null = isTruncated ? plainText : null;

  return (
    <Stack direction="row" spacing={0}>
      <Tooltip label={tooltipText} ariaLabel={tooltipText || ''}>
        <SecondaryBody
          dangerouslySetInnerHTML={{
            __html: displayText,
          }}
        />
      </Tooltip>
      <IconButton
        icon={CloseIcon}
        label={t('remove')}
        onClick={onRemove}
        size="sm"
        flat
      />
    </Stack>
  );
};

export default ClearableFilter;
